import { RenderingStatus, SpinnerType } from "Enumerations";
import React from "react";

import { ProgressBar, Skeleton, SkeletonItem, Spinner } from "@fluentui/react-components";
import { ArrowClockwiseRegular } from "@fluentui/react-icons";

import { M365Alert } from "../Alert/M365Alert";
import { useStyles } from "./WithStatus.styles";
import { WithStatusProps } from "./WithStatus.types";
import { getThrottledSource, wasRecentlyThrottled } from "Shared/Utils";

const defaultErrorMessage = "Something went wrong.";

const throttledMessages: Record<string, string> = {
    kusto: "Kusto throttling: service is experiencing high demand. Please try again in a few moments."
};
const defaultThrottledMessage = "Service throttling: experiencing high demand. Please try again in a few moments.";

function getErrorInfo(errorMessage: string | undefined): { message: string; intent: "warning" | "error" } {
    const isThrottled = wasRecentlyThrottled();
    if (!isThrottled) {
        return { message: errorMessage || defaultErrorMessage, intent: "error" };
    }
    const source = getThrottledSource();
    const throttleMsg = source ? (throttledMessages[source] ?? defaultThrottledMessage) : defaultThrottledMessage;
    return { message: errorMessage || throttleMsg, intent: "warning" };
}

export const withStatus =
    <T extends object>(Component: React.ComponentType<T>, spinnerTypeProp?: SpinnerType): React.FC<T & WithStatusProps> =>
    (props: T & WithStatusProps) => {
        const { loadingStatus, noData, loadingMessage, errorMessage, noDataMessage, retry, spinnerType, classNames } = props;
        const retryAction = retry ? { onClick: retry, icon: <ArrowClockwiseRegular /> } : undefined;
        const styles = useStyles();

        if (loadingStatus === RenderingStatus.Waiting) {
            switch (spinnerTypeProp || spinnerType) {
                case SpinnerType.Progress:
                    return <ProgressBar aria-busy="true" aria-label="Loading Content" />;
                case SpinnerType.None:
                    return <div />;
                case SpinnerType.Skeleton:
                    return (
                        <Skeleton className={styles.skeleton}>
                            <SkeletonItem />
                        </Skeleton>
                    );
                case SpinnerType.Spinner:
                default:
                    return <Spinner aria-busy="true" aria-label="Loading Content" label={loadingMessage} />;
            }
        }

        if (loadingStatus === RenderingStatus.Error) {
            if (errorMessage !== undefined && typeof errorMessage !== "string") {
                return errorMessage;
            }
            const errorInfo = getErrorInfo(typeof errorMessage === "string" ? errorMessage : undefined);
            return <M365Alert message={errorInfo.message} intent={errorInfo.intent} action={retryAction} className={classNames?.message} />;
        }

        if (noData) {
            return noDataMessage !== undefined && typeof noDataMessage !== "string" ? (
                noDataMessage
            ) : (
                <M365Alert message={noDataMessage || "No data"} intent="info" className={classNames?.message} />
            );
        }

        return <Component {...props} />;
    };
